import React, { useEffect, useMemo, useState } from "react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { db, functions } from "../firebase/firebase";
import { extractConfigSnapshotFromModele } from "../services/data";

export default function ShareLinksModal({
  open,
  template: templateProp,
  modele,
  user,
  onClose,
}) {
  const template = templateProp ?? modele;
  // ✅ Si tu as une row relation (id=rowId) et modeleId=le vrai id du modèle,
  // on doit préférer modeleId
  const templateId = template?.modeleId || template?.id || template?.templateId;
  const templateName = template?.nom || template?.title || template?.templateName || "Template";
  const existingToken = template?.shareToken || template?.share_token || null;

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [linkDoc, setLinkDoc] = useState(null);
  const [token, setToken] = useState(existingToken);

  const [expiresInDays, setExpiresInDays] = useState(0);
  const [maxSubmissions, setMaxSubmissions] = useState("");
  const [status, setStatus] = useState("active");

  const shareUrl = useMemo(() => {
    if (!token) return "";
    return `${window.location.origin}/s/${token}`;
  }, [token]);

  useEffect(() => {
    if (!open) return;

    setLinkDoc(null);
    setExpiresInDays(0);
    setMaxSubmissions("");
    setStatus("active");

    // ✅ Reset token to the current template's token (or null)
    setToken(existingToken || null);
  }, [open, existingToken]);

  useEffect(() => {
    if (!open) return;
    if (!existingToken) return;
    setToken((prev) => prev || existingToken);
  }, [open, existingToken]);

  useEffect(() => {
    if (!open) return;
    if (token) return;
    if (!user?.uid || !templateId) return;

    (async () => {
      try {
        const mRef = doc(db, "users", user.uid, "modeles", templateId);
        const mSnap = await getDoc(mRef);
        if (mSnap.exists()) {
          const st = mSnap.data()?.shareToken || mSnap.data()?.share_token;
          if (st) setToken((prev) => prev || st);
        }
      } catch (e) {
        console.warn("ShareLinksModal: impossible de charger shareToken", e);
      }
    })();
  }, [open, token, user?.uid, templateId]);

  useEffect(() => {
    if (!open) return;
    if (!token) return;

    (async () => {
      setLoading(true);
      try {
        const ref = doc(db, "public_form_links", token);
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          setLinkDoc(data);
          setStatus(data.status || "active");
          setMaxSubmissions(
            data.maxSubmissions === null || data.maxSubmissions === undefined
              ? ""
              : String(data.maxSubmissions)
          );
        } else {
          setLinkDoc(null);
        }
      } catch (e) {
        console.error(e);
        setLinkDoc(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [open, token]);

  const close = () => {
    onClose?.();
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      alert("Lien copié !");
    } catch {
      alert("Impossible de copier automatiquement. Copie manuelle: " + shareUrl);
    }
  };

  const applyUpdates = async (partial) => {
    if (!token) return;
    const ref = doc(db, "public_form_links", token);
    await updateDoc(ref, partial);
    // reload
    const snap = await getDoc(ref);
    setLinkDoc(snap.exists() ? snap.data() : null);
  };

  const toggleStatus = async () => {
    if (!token) return;
    const next = (linkDoc?.status || status) === "active" ? "disabled" : "active";
    await applyUpdates({ status: next });
    setStatus(next);
  };

  const updateLimits = async () => {
    if (!token) return;

    const ms = Number(maxSubmissions);
    const max = Number.isFinite(ms) && ms > 0 ? ms : null;

    let expiresAt = null;
    const days = Number(expiresInDays);
    if (Number.isFinite(days) && days > 0) {
      expiresAt = new Date(Date.now() + Math.floor(days) * 24 * 60 * 60 * 1000);
    }

    await applyUpdates({ maxSubmissions: max, expiresAt });
    alert("Paramètres enregistrés.");
  };

  const createOrGetLink = async () => {
    if (!user?.uid || !templateId) return;

    setCreating(true);
    try {
      const createFn = httpsCallable(functions, "createShareLink");

      // IMPORTANT: pour le 1er lien, il faut configSnapshot
      const snapshot = await extractConfigSnapshotFromModele(template);

      const days = Number(expiresInDays);
      const ms = Number(maxSubmissions);

      const payload = {
        modeleId: templateId,
        title: templateName,
        status: "active",
        configSnapshot: snapshot,
        expiresInDays: Number.isFinite(days) ? days : 0,
        maxSubmissions: Number.isFinite(ms) ? ms : null,
      };

      const res = await createFn(payload);
      const newToken = res?.data?.token;
      if (!newToken) throw new Error("Token manquant dans la réponse.");

      setToken(newToken);

      try {
        const mRef = doc(db, "users", user.uid, "modeles", templateId);
        // ✅ On utilise setDoc avec merge pour être sûr (ou updateDoc si on est certain qu'il existe)
        // Ici updateDoc est correct car le modèle existe forcément
        await updateDoc(mRef, { shareToken: newToken });
      } catch (e) {
        console.warn("ShareLinksModal: update shareToken failed", e);
      }

      // Fetch doc
      const ref = doc(db, "public_form_links", newToken);
      const snap = await getDoc(ref);
      setLinkDoc(snap.exists() ? snap.data() : null);

      alert("Lien prêt !");
    } catch (e) {
      console.error(e);
      alert("Erreur création lien : " + (e?.message || "inconnue"));
    } finally {
      setCreating(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`modal-overlay ${open ? "show" : ""}`} onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">
            <i className="fas fa-share-alt" /> Partager — {templateName}
          </div>
          <div className="modal-close" onClick={close}>
            <i className="fas fa-times" />
          </div>
        </div>

        <div className="modal-body">
          {!templateId ? (
            <p className="text-muted">Template invalide (id manquant).</p>
          ) : loading ? (
            <p>Chargement…</p>
          ) : !token ? (
            <div>
              <p className="text-muted" style={{ marginBottom: 12 }}>
                Aucun lien de partage pour ce template. Crée-le une seule fois (ensuite tu peux activer/désactiver).
              </p>

              <div style={{ display: "grid", gap: 10 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  <span className="text-muted">Expiration (jours) — 0 = pas d'expiration</span>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                  />
                </label>

                <label style={{ display: "grid", gap: 6 }}>
                  <span className="text-muted">Nombre max de réponses — vide = illimité</span>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={maxSubmissions}
                    onChange={(e) => setMaxSubmissions(e.target.value)}
                  />
                </label>

                <button className="btn btn-primary" onClick={createOrGetLink} disabled={creating}>
                  <i className="fas fa-link" /> {creating ? "Création..." : "Créer le lien"}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: "grid", gap: 14 }}>
              <div>
                <div className="text-muted" style={{ marginBottom: 6 }}>
                  Lien unique du template
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <input className="form-control" value={shareUrl} readOnly />
                  <button className="btn btn-outline" onClick={copy} title="Copier">
                    <i className="fas fa-copy" />
                  </button>
                  <a className="btn btn-outline" href={shareUrl} target="_blank" rel="noreferrer">
                    <i className="fas fa-external-link-alt" />
                  </a>
                </div>
              </div>

              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                <span className="text-muted">Statut:</span>
                <span
                  style={{
                    padding: "4px 10px",
                    borderRadius: 999,
                    background: (linkDoc?.status || status) === "active" ? "#d1fae5" : "#fee2e2",
                  }}
                >
                  {(linkDoc?.status || status) === "active" ? "Actif" : "Désactivé"}
                </span>

                <button className="btn btn-outline" onClick={toggleStatus}>
                  <i className="fas fa-toggle-on" />{" "}
                  {(linkDoc?.status || status) === "active" ? "Désactiver" : "Activer"}
                </button>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <label style={{ display: "grid", gap: 6 }}>
                  <span className="text-muted">Expiration (jours) — 0 = pas d'expiration</span>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={expiresInDays}
                    onChange={(e) => setExpiresInDays(e.target.value)}
                  />
                  {linkDoc?.expiresAt?.toDate && (
                    <span className="text-muted">
                      Actuel: {linkDoc.expiresAt.toDate().toLocaleString()}
                    </span>
                  )}
                </label>

                <label style={{ display: "grid", gap: 6 }}>
                  <span className="text-muted">Nombre max de réponses — vide = illimité</span>
                  <input
                    className="form-control"
                    type="number"
                    min="0"
                    value={maxSubmissions}
                    onChange={(e) => setMaxSubmissions(e.target.value)}
                  />
                </label>

                <button className="btn btn-primary" onClick={updateLimits}>
                  <i className="fas fa-save" /> Enregistrer
                </button>
              </div>

              {!linkDoc?.configSnapshot && (
                <div className="text-muted" style={{ marginTop: 6 }}>
                  ⚠️ configSnapshot manquant dans le lien (si c'est un ancien lien, recrée-le).
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-outline" onClick={close}>
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
